import { Injectable, OnInit, PLATFORM_ID,Inject, afterNextRender  } from '@angular/core';
import { isPlatformServer, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { FormDataMapperService } from './form-data-mapper.service';
import { Console } from 'node:console';
import { TypescriptValidationsService } from './typescript-validations.service'
import { WindowService } from './window.service';
import { AddressResolutionService } from './address-resolution.service';
import { TelemetryService } from './telemetry.service';

@Injectable({
  providedIn: 'root'
})
export class FormDataService {
  private formDataSubject = new BehaviorSubject<any>({});
  private currentStepSubject = new BehaviorSubject<number>(0);
  private urlParams: { [key: string]: string } = {};
  private developerMode = false;
  private usePlacesNew = false;
  
  formData$ = this.formDataSubject.asObservable();
  currentStep$ = this.currentStepSubject.asObservable();

  private storageKeyPrefix = 'formData_';
  private stepKeyPrefix = 'currentStep_';
  private currentStorageKey: string = "";
  
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,    
    private http: HttpClient,
    private FormDataMapperService: FormDataMapperService,
    private leadMapper: FormDataMapperService,
    private typescriptValidationsService: TypescriptValidationsService,
    private window: WindowService,
    private activatedRoute: ActivatedRoute,
        private addressResolutionService: AddressResolutionService,
    private telemetryService: TelemetryService
    
  ) {
    const qp = this.activatedRoute.snapshot.queryParams;
    const truthy = (v: string | undefined) => v === '1' || v === 'true' || v === 'yes';
    this.usePlacesNew = truthy(qp['usePlacesNew']);
    this.developerMode = truthy(qp['developer']);
  }

  private get isBrowser(): boolean {
    return !!this.window.window;
  }

  private get storage(): Storage | null {
    return isPlatformBrowser(this.platformId) ? localStorage : null;
  }


  private debugLog(msg: string, ...args: any[]): void {
    if (this.developerMode && this.isBrowser) {
      console.log(msg, ...args);
    }
  }
  
  initializeUrlParams(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (this.window.locationSearch) {
        const params = new URLSearchParams(this.window.locationSearch);
        params.forEach((value, key) => {
          this.urlParams[key] = this.convertUrlValue(value);
        });
      }
    }
  }

  private getStorageKey(identifier?: string): string {
    return identifier ? `${this.storageKeyPrefix}${identifier}` : `${this.storageKeyPrefix}default`;
  }  

  private getStepKey(identifier?: string): string {
    return identifier ? `${this.stepKeyPrefix}${identifier}` : `${this.stepKeyPrefix}default`;
  }

  async initializeFormData() {    
    if (!isPlatformBrowser(this.platformId)) {
      return; 

    }


    const urlParams = new URLSearchParams(this.window.locationSearch);
    const recordId = urlParams.get('RecordID');
    const lpkey = urlParams.get('lpkey') || urlParams.get('LPKey') || urlParams.get('LPKEY') || urlParams.get('lpKey');
    const landingPageID = urlParams.get('landingPageID') || 'default';
    
    var genericGuid = null;
    //genericGuid = localStorage.getItem('Session-Guid'); //JLFranco
    genericGuid = this.storage?.getItem('Session-Guid') ?? null;
    
    if(genericGuid == null){  
        genericGuid = this.generateGuid();
        //localStorage.setItem('Session-Guid', genericGuid); //JLFranco
        this.storage?.setItem('Session-Guid', genericGuid);
    } else {
        //genericGuid = localStorage.getItem('Session-Guid'); //JLFranco
        genericGuid = this.storage?.getItem('Session-Guid') ?? null;
    }
    

    const uniqueIndentifier = urlParams.get('confirmation') || urlParams.get('phone') || urlParams.get('CustomVar3') || genericGuid;
    const urlIdentifier = landingPageID + '_' + uniqueIndentifier;
    
    this.currentStorageKey = this.getStorageKey(urlIdentifier);
    const stepKey = this.getStepKey(urlIdentifier);

    //const savedData = localStorage.getItem(this.currentStorageKey); //JLFranco
    const savedData = this.storage?.getItem(this.currentStorageKey) ?? null;


    if (savedData) {
      this.formDataSubject.next(JSON.parse(savedData));
    } else if (recordId) {
      if(recordId == '0') {
        this.populateFromUrlParams();
      } else {
        try {      
            
          const data = await this.fetchFormData();
          this.updateFormData(this.urlParams);
                
          if(data && data.jsonValue) {
            try {
              const parsedData = JSON.parse(data.jsonValue);            
              this.updateFormData(parsedData);
              if(parsedData.Phone == '' && parsedData.Email == '' && parsedData.FName == '') {
                this.updateFormData(data);
              }
            } catch (e) {
              this.updateFormData(data);
              console.error('Error parsing JSON value:', e);
            }
          } else {
        
            this.updateFormData(data);
          }
  
          if(data.StepID && !isNaN(Number(data.StepID)) && Number(data.StepID) > 0){ 
            this.updateCurrentStep(data.StepID || 0);
          }
          
  
          
        } catch (error) {        
          console.error('Error fetching initial data', error);
          this.populateFromUrlParams();
        }
      }
    } else if(lpkey) {
      try {        
        this.updateFormData(this.urlParams);

        const data = await this.fetchLeadPointFormData(lpkey).then(res => {   
        var _res = JSON.parse(res);                
        return this.leadMapper.mapLeadToForm(_res);        
        });        
        this.updateFormData(data);

      } catch (error) {
        console.error('Error fetching initial data', error);
        this.populateFromUrlParams();
      }
    } else {
        this.populateFromUrlParams();
    }
    //const savedStep = localStorage.getItem(stepKey); //JLFranco
    const savedStep = this.storage?.getItem(stepKey) ?? null;
    if (savedStep) {
      this.currentStepSubject.next(Number(savedStep));
    }
  }

  private populateFromUrlParams(): void {
    if (Object.keys(this.urlParams).length > 0) {
        const formRelevantParams = {} as any;

        if (Object.keys(this.urlParams).length > 0) {
            this.updateFormData(this.urlParams);
        }
    }
  }

  private convertUrlValue(value: string): any {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
    

    if (!isNaN(Number(value)) && value.trim() !== '') {
      return Number(value);
    }
    

    try {
      return JSON.parse(value);
    } catch (e) {

      return value;
    }
  }

  updateFormData(data: any): void {
    const currentData = this.formDataSubject.value;
    const newData = { ...currentData };
    
    Object.keys(data).forEach(key => {
      newData[key] = data[key];
    });

    var typescriptValidations = this.typescriptValidationsService.getCachedFormBrandSettings()
    if(typescriptValidations && typescriptValidations.length > 0) {
      typescriptValidations.forEach(typescriptValidation => {
        var functionContent = typescriptValidation.fields.functionContent as unknown as string;
        var jsonContent: any = typescriptValidation.fields.jsonContent;

        var params = {};
        if (jsonContent) {
          jsonContent?.params?.fieldKeys.forEach((fieldKey: any) => {
            var vp = { [fieldKey]: newData[fieldKey] };
            params = { ...params, ...vp };
          });
        }
        
        var response = this.typescriptValidationsService.processStringFuncion(functionContent, params)
        
        if (response && newData) {
          Object.keys(response).forEach(key => {
            if (newData.hasOwnProperty(key)) {

              if(response[key] != undefined)
              newData[key] = response[key];
            }
            else {
              newData[key] = response[key];
            }
          });
        }
        
        });
    }

    this.formDataSubject.next(newData);
    //localStorage.setItem(this.currentStorageKey, JSON.stringify(newData)); //JLFranco
    this.storage?.setItem(this.currentStorageKey, JSON.stringify(newData));
  } 

  updateCurrentStep(step: number): void {
    const stepKey = this.getStepKey(this.getIdentifierFromKey(this.currentStorageKey));
    this.currentStepSubject.next(step);
    //localStorage.setItem(stepKey, step.toString()); //JLFranco
    this.storage?.setItem(stepKey, step.toString());
  }

  updateIdentifier(newIdentifier: string): void {
    const oldData = this.formDataSubject.value;
    const oldStep = this.currentStepSubject.value;
    
    const newStorageKey = this.getStorageKey(newIdentifier);
    const newStepKey = this.getStepKey(newIdentifier);

    if (!this.currentStorageKey.endsWith('default')) {
      //localStorage.removeItem(this.currentStorageKey); //JLFranco
      this.storage?.removeItem(this.currentStorageKey);
      const oldStepKey = this.getStepKey(this.getIdentifierFromKey(this.currentStorageKey));
      //localStorage.removeItem(oldStepKey); //JLFranco
      this.storage?.removeItem(oldStepKey);
    }

    this.currentStorageKey = newStorageKey;
  }

  private getIdentifierFromKey(key: string): string | undefined {
    const prefixLength = this.storageKeyPrefix.length;
    return key.startsWith(this.storageKeyPrefix) && key !== `${this.storageKeyPrefix}default`
      ? key.substring(prefixLength)
      : undefined;
  }

  getCurrentFormData() {
    return this.formDataSubject.value;
  }

  getCurrentStep() {
    return this.currentStepSubject.value;
  }

  clearAllData() {
    
    //localStorage.removeItem(this.currentStorageKey); //JLFranco
    this.storage?.removeItem(this.currentStorageKey);
    const stepKey = this.getStepKey(this.getIdentifierFromKey(this.currentStorageKey));
    //localStorage.removeItem(stepKey); //JLFranco
    this.storage?.removeItem(stepKey);
    
    this.formDataSubject.next({});
    this.currentStepSubject.next(0);
  }

  clearStorageByLoad() {
    this.storage?.removeItem('sliderTouched');
  }

  private async fetchFormData(): Promise<any> {

    const utm_campaign = this.urlParams['utm_campaign'] || '';

    var cmp = utm_campaign.split('_');
    var lastOffer = 0;
    if (cmp.length > 1 && cmp[0] === 'RMK') {
      lastOffer = Number(cmp[1]); 

    }

    var stepID = 1;
    if(lastOffer > 0){
      stepID = lastOffer;
    }
    
    var AnyToken = null;
    AnyToken = '39c1d50f-28b6-4472-b4a5-69a73f6080e4';
    
    const dataObj = {
      RecID: this.urlParams['RecordID'],
      Site: this.window.locationOrigin + this.window.locationPathname,
      VerticalType: 1,
      SourceID: this.urlParams['SourceID'], 
      DropDate: this.urlParams['utm_term'], 
      URLRequest: this.window.documentURL,
      Token: AnyToken, 
      StepID: stepID
    };

    const config = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'withCredentials': 'true',
        'Authorization': 'true',
        'Token': AnyToken
      })
    };

    const timer = this.telemetryService.startTimer();
    try {
      // Telemetry: measure GetRecordByID API duration and status.
      this.debugLog('[MAPS-DEBUG] [FORM-DATA-SRV] [FetchFormData] calling GetRecordByID', dataObj);
      const response = await this.http.post<any>(`https://landinghelper.azurewebsites.net/api/GetRecordByID`, JSON.stringify(dataObj),config).toPromise();
      this.telemetryService.trackApiCallResult(
        'GetRecordByID',
        'https://landinghelper.azurewebsites.net/api/GetRecordByID',
        this.telemetryService.stopTimer(timer),
        true,
        200,
        {
          hasRecordId: !!this.urlParams['RecordID'],
          hasSourceId: !!this.urlParams['SourceID'],
        }
      );
      return response;

    } catch (error) {
      this.telemetryService.trackApiCallResult(
        'GetRecordByID',
        'https://landinghelper.azurewebsites.net/api/GetRecordByID',
        this.telemetryService.stopTimer(timer),
        false,
        (error as any)?.status,
        {
          hasRecordId: !!this.urlParams['RecordID'],
          hasSourceId: !!this.urlParams['SourceID'],
        }
      );
      console.error('Error fetching form data:', error);
      throw error;
    }
  }

  private async _fetchLeadPointFormData(LeadID: string): Promise<any> {

    const config = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'withCredentials': 'true',
        'Authorization': 'true'
      })
    };

    try {
      return await this.http.post<any>(`https://member.leadpoint.com/lead/getDetails.est?randomToken=${LeadID}`, null, config).toPromise();

    } catch (error) {
      console.error('Error fetching form data:', error);
      throw error;
    }
  }  

  private async fetchLeadPointFormData(LeadID: string): Promise<any> {
    const config = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'true'
      })
    };

    const timer = this.telemetryService.startTimer();
    try {
      // return await this.http.post<any>(`https://e9cd-181-189-154-153.ngrok-free.app/api/GetRecordByLPKey?lpKey=${LeadID}`, null, config).toPromise();

      // Telemetry: measure GetRecordByLPKey API duration and status.
      this.debugLog('[MAPS-DEBUG] [FORM-DATA-SRV] [fetchLeadPointFormData] calling GetRecordByLPKey', { LeadID });
      const response = await this.http.post<any>(`https://apihelper.azurewebsites.net/api/GetRecordByLPKey?lpKey=${LeadID}`, null, config).toPromise();
      this.telemetryService.trackApiCallResult(
        'GetRecordByLPKey',
        'https://apihelper.azurewebsites.net/api/GetRecordByLPKey',
        this.telemetryService.stopTimer(timer),
        true,
        200,
        { hasLeadId: !!LeadID }
      );
      return response;

    } catch (error) {
      this.telemetryService.trackApiCallResult(
        'GetRecordByLPKey',
        'https://apihelper.azurewebsites.net/api/GetRecordByLPKey',
        this.telemetryService.stopTimer(timer),
        false,
        (error as any)?.status,
        { hasLeadId: !!LeadID }
      );
      console.error('Error fetching form data:', error);
      throw error;
    }
  }  

  static strictUsPhoneValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null;
      
      const digitsOnly = control.value.replace(/\D/g, '');
      
      const isValid = /^\d{10}$/.test(digitsOnly);
      
      return isValid ? null : { invalidUsPhone: true };
    };
  }

  static isValidUsPhone(phone: string): boolean {
    return /^\d{10}$/.test(phone.replace(/\D/g, ''));
  }

  generateGuid(): string {
    return crypto.randomUUID(); 
  }

}
